import { __decorate } from "tslib";
import { localized, msg, str } from '@lit/localize';
import debounce from 'debounce';
import { html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { SlotController } from '../../shared/controllers/slot.controller.js';
import { HelixTextFormControl } from '../../shared/forms/helix-text-form-control.js';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { formControlStyles } from '../../shared/styles/form-control.styles.js';
import { textareaStyles } from './textarea.styles.js';
import '../divider/divider.js';
import '../icon/icon.js';
/**
 * Textarea component to input a longer text values.
 *
 * @slot label – Label for the textarea, appears on top of it. @see HlxTextarea#label
 * @slot error-text – Custom error text that appears below the textarea. @see HlxTextarea#errorText
 * @slot help-text – Helper text that appears below the textarea in smaller font. @see HlxTextarea#helpText
 *
 * @event hlx-blur - Triggers when the control loses focus.
 * @event hlx-change - Triggers when the control value changes.
 * @event hlx-focus - Triggers when the control gains focus.
 * @event hlx-input - Triggers when the control receives input.
 * @event hlx-invalid - Triggers when the form control has been checked for validity and its constraints aren't satisfied.
 *
 * @csspart hlx-form-control - The form control container.
 * @csspart hlx-form-control-label - The label slot.
 * @csspart hlx-form-control-input - The input element.
 * @csspart hlx-form-control-error - The error container.
 * @csspart hlx-form-control-help-text - The help container.
 * @csspart hlx-form-control-char-counter - The character counter container.
 */
let HlxTextarea = class HlxTextarea extends HelixTextFormControl {
    constructor() {
        super(...arguments);
        this.hasFocus = false;
        this.resized = false;
        /**
         * Debounce delay in milliseconds for which a11y announcer will wait and not announce how many
         * characters left until max-length. 1000ms by default
         */
        this.counterAnnounceDelay = 1000;
        this.value = '';
        /** Label for the textarea, appears on top of it. Also available as `label` slot */
        this.label = '';
        /**
         * Rows count in textarea, 4 by default.
         * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/textarea#rows | textarea#rows on MDN}}.
         */
        this.rows = 4;
        this.maxlength = null;
        /**
         * Placeholder text, appears as somewhat more transparent text inside the text area, until user
         * start entering their value
         */
        this.placeholder = '';
        /** Helper text that appears below the textarea in smaller font. */
        this.helpText = '';
        /** Disables the ability to enter new text in textarea but doesn't show it as `disabled` */
        this.readonly = false;
        /** Accessible title of a component */
        this.title = '';
        /**
         * Allows browser to assist with filling the textarea, "off" by default
         * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete | autocomplete on MDN}}
         */
        this.autocomplete = 'off';
        /** Size of a textarea, can be `small` or `medium`; medium by default */
        this.size = 'medium';
        this.autosize = false;
        this.autosizeMinRows = 1;
        this.autosizeMaxRows = 4;
        this.suffixDivider = false;
        this.inputId = crypto.randomUUID();
        this.slotController = new SlotController(this, 'label', 'suffix', 'help-text');
    }
    render() {
        const hasLabel = this.label || this.slotController.hasSlot('label');
        const hasSuffix = this.slotController.hasSlot('suffix');
        const hasCounter = this.maxlength !== null;
        const hasHelpText = this.helpText || this.slotController.hasSlot('help-text') || hasCounter;
        return html `
            <div
                part="hlx-form-control"
                class="${classMap({
            ...this.validation.classMap,
            'hlx-textarea-form-control': true,
            'hlx-form-control-md': this.size === 'medium',
            'hlx-form-control-has-label': hasLabel,
            'hlx-form-control-has-suffix': hasSuffix,
            'hlx-form-control-has-help-text': hasHelpText,
            'hlx-form-control-has-counter': hasCounter,
            'hlx-form-control-resized': this.resized,
            'hlx-form-control-autosize': this.autosize,
        })}">
                <label
                    part="hlx-form-control-label"
                    class="${classMap({
            'hlx-form-control-label': true,
            'hlx-form-control-label-disabled': this.disabled,
        })}"
                    for=${this.inputId}>
                    <slot name="label">${this.label}</slot>
                </label>

                <div
                    part="hlx-form-control-base"
                    class="${classMap({
            'hlx-textarea': true,
            'hlx-textarea-disabled': this.disabled,
            'hlx-textarea-focused': this.hasFocus,
            'hlx-textarea-readonly': this.readonly,
        })}"
                    style="${styleMap({
            minHeight: this.calcResizedMinHeight(),
        })}">
                    <div class="hlx-textarea-control-wrapper" @click=${this.handleWrapperClick}>
                        <textarea
                            part="hlx-form-control-input"
                            id=${this.inputId}
                            type="text"
                            class="${classMap({
            'hlx-textarea-control': true,
        })}"
                            .value=${live(this.value)}
                            rows=${this.autosize || this.resized ? 1 : this.rows}
                            name=${ifDefined(this.name)}
                            placeholder=${ifDefined(this.placeholder)}
                            title=${this.title}
                            ?required=${this.required}
                            minlength=${ifDefined(this.minlength)}
                            autocomplete=${ifDefined(this.autocomplete)}
                            ?disabled=${this.disabled}
                            ?readonly=${this.readonly}
                            @change=${this.handleChange}
                            @blur=${this.handleBlur}
                            @focus=${this.handleFocus}
                            @input=${this.handleInput}></textarea>
                    </div>

                    ${this.suffixDivider && hasSuffix ?
            html `
                            <hlx-divider
                                part="hlx-textarea-suffix-divider"
                                class="hlx-textarea-suffix-divider"></hlx-divider>
                        `
            : nothing}

                    <slot
                        name="suffix"
                        part="hlx-textarea-suffix"
                        class="hlx-textarea-suffix"></slot>
                </div>

                ${this.validation.renderErrorSlot()}

                <div
                    id="help-text"
                    part="hlx-form-control-help-text"
                    class="${classMap({
            'hlx-form-control-help-text': true,
            'hlx-form-control-help-text-disabled': this.disabled,
        })}">
                    <slot name="help-text">${this.helpText}</slot>
                    <div class="hlx-textarea-counter" part="hlx-form-control-char-counter">
                        ${`${this.value?.length || 0}/${this.maxlength}`}
                        <span
                            class="hlx-textarea-counter-alert"
                            aria-live="polite"
                            role="log"></span>
                    </div>
                </div>
            </div>
        `;
    }
    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        this.calculateLineHeight();
        if (!this.observer) {
            this.observer = new ResizeObserver(() => {
                if (this.baseEl.style.width && this.baseEl.style.width !== 'auto') {
                    this.resized = true;
                }
                this.resizeToFitContent();
            });
            this.observer.observe(this.baseEl);
        }
    }
    announce() { }
    updated(changes) {
        if (changes.has('autosize') && !!changes.get('autosize') !== !!this.autosize) {
            this.autosize ? this.resizeToFitContent() : this.resetToInitialHeight();
        }
        if (changes.has('autosizeMinRows')) {
            this.setMinHeight();
        }
        if (changes.has('autosizeMaxRows')) {
            this.setMaxHeight();
        }
        if (changes.has('counterAnnounceDelay')) {
            this.announce = debounce(() => {
                // voiceover announces only changes that are done
                // directly via DOM modification, i.e. it doesn't notice
                // changes made in render()
                this.announcer.textContent = this.getCounterMessage();
            }, this.counterAnnounceDelay);
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.observer) {
            this.observer.disconnect();
        }
    }
    handleChange() {
        this.value = this.input.value;
        this.emit('hlx-change');
    }
    handleBlur() {
        this.hasFocus = false;
        this.emit('hlx-blur');
    }
    handleFocus() {
        this.hasFocus = true;
        this.emit('hlx-focus');
    }
    handleInput() {
        this.resizeToFitContent();
        super.handleInput();
    }
    handleWrapperClick() {
        this.input?.focus();
    }
    calcResizedMinHeight() {
        // With suffix: topPadding(16) + cachedLineHeight(24) + suffixHeight(48) = 88px
        // Without suffix: topAndBottomPadding(32) + cachedLineHeight(24) = 56px
        if (this.slotController.hasSlot('suffix')) {
            return (16 +
                (this.cachedLineHeight || 0) +
                (this.suffixEl?.getBoundingClientRect()?.height || 0) +
                'px');
        }
        else {
            return 16 * 2 + (this.cachedLineHeight || 0) + 'px';
        }
    }
    resizeToFitContent() {
        if (!this.autosize) {
            return;
        }
        this.calculateLineHeight();
        if (!this.cachedLineHeight) {
            return;
        }
        this.resized = false;
        this.baseEl.style.height = 'auto';
        this.baseEl.style.width = 'auto';
        this.input.style.height = '';
        this.input.style.height = this.input.scrollHeight + 'px';
        this.setMinHeight();
        this.setMaxHeight();
    }
    resetToInitialHeight() {
        this.input.style.height = 'auto';
        this.input.style.minHeight = 'auto';
        this.input.style.maxHeight = 'none';
    }
    calculateLineHeight() {
        if (this.cachedLineHeight) {
            return;
        }
        const inputClone = this.input.cloneNode(false);
        inputClone.rows = 1;
        inputClone.style.position = 'absolute';
        inputClone.style.visibility = 'hidden';
        inputClone.style.border = 'none';
        inputClone.style.padding = '0';
        inputClone.style.height = '';
        inputClone.style.minHeight = '';
        inputClone.style.maxHeight = '';
        inputClone.style.overflow = 'hidden';
        this.input.parentNode.appendChild(inputClone);
        this.cachedLineHeight = inputClone.clientHeight;
        inputClone.remove();
        this.setMinHeight();
        this.setMaxHeight();
    }
    setMinHeight() {
        if (!this.autosize) {
            return;
        }
        const minHeight = this.autosizeMinRows && this.cachedLineHeight ?
            `${this.autosizeMinRows * this.cachedLineHeight}px`
            : null;
        if (minHeight) {
            this.input.style.minHeight = minHeight;
        }
    }
    setMaxHeight() {
        if (!this.autosize) {
            return;
        }
        const maxHeight = this.autosizeMaxRows && this.cachedLineHeight ?
            `${this.autosizeMaxRows * this.cachedLineHeight}px`
            : null;
        if (maxHeight) {
            this.input.style.maxHeight = maxHeight;
        }
    }
    getDefaultErrorMessage(validity) {
        if (this.validation.hasCustomValidity) {
            return '';
        }
        if (this.validity.valueMissing) {
            return msg('Please fill out this field.');
        }
        else if (this.validity.tooShort) {
            const need = getCharsStr(this.minlength);
            const current = getCharsStr(this.input.value.length);
            return msg(str `Please use at least ${need} (you are currently using ${current}).`);
        }
        else if (this.validity.tooLong) {
            const exceed = getCharsStr(this.input.value.length - this.maxlength);
            return msg(str `${exceed} too long.`);
        }
        return '';
    }
    getCounterMessage() {
        if (!this.maxlength) {
            return '';
        }
        const diff = this.maxlength - this.input.value.length;
        if (diff === 1) {
            return msg(str `1 character remaining`);
        }
        else if (diff >= 0) {
            return msg(str `${diff} characters remaining`);
        }
        else if (diff === -1) {
            return msg(str `1 character too long`);
        }
        else {
            return msg(str `${Math.abs(diff)} characters too long`);
        }
    }
};
HlxTextarea.styles = [componentStyles, formControlStyles, textareaStyles];
__decorate([
    query('.hlx-textarea-counter-alert')
], HlxTextarea.prototype, "announcer", void 0);
__decorate([
    query('.hlx-textarea')
], HlxTextarea.prototype, "baseEl", void 0);
__decorate([
    query('.hlx-textarea-control')
], HlxTextarea.prototype, "input", void 0);
__decorate([
    query('.hlx-textarea-suffix')
], HlxTextarea.prototype, "suffixEl", void 0);
__decorate([
    query('#counter-announcer')
], HlxTextarea.prototype, "counterAnnouncer", void 0);
__decorate([
    state()
], HlxTextarea.prototype, "hasFocus", void 0);
__decorate([
    state()
], HlxTextarea.prototype, "resized", void 0);
__decorate([
    property({ type: Number, attribute: 'counter-announce-delay' })
], HlxTextarea.prototype, "counterAnnounceDelay", void 0);
__decorate([
    property({ type: String })
], HlxTextarea.prototype, "value", void 0);
__decorate([
    property({ type: String })
], HlxTextarea.prototype, "label", void 0);
__decorate([
    property({ type: Number })
], HlxTextarea.prototype, "rows", void 0);
__decorate([
    property({ type: Number, attribute: 'maxlength' })
], HlxTextarea.prototype, "maxlength", void 0);
__decorate([
    property({ type: Number, reflect: true })
], HlxTextarea.prototype, "minlength", void 0);
__decorate([
    property({ type: String })
], HlxTextarea.prototype, "placeholder", void 0);
__decorate([
    property({ type: String, attribute: 'help-text' })
], HlxTextarea.prototype, "helpText", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxTextarea.prototype, "readonly", void 0);
__decorate([
    property({ type: String })
], HlxTextarea.prototype, "title", void 0);
__decorate([
    property({ type: String })
], HlxTextarea.prototype, "autocomplete", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxTextarea.prototype, "size", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxTextarea.prototype, "autosize", void 0);
__decorate([
    property({ type: Number, attribute: 'autosize-min-rows' })
], HlxTextarea.prototype, "autosizeMinRows", void 0);
__decorate([
    property({ type: Number, attribute: 'autosize-max-rows' })
], HlxTextarea.prototype, "autosizeMaxRows", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'suffix-divider' })
], HlxTextarea.prototype, "suffixDivider", void 0);
HlxTextarea = __decorate([
    customElement('hlx-textarea'),
    localized()
], HlxTextarea);
export { HlxTextarea };
// This is not general case, but works for English & Spanish, which we support
// Unfortunately Lit-localize doesn't support pluralization ATM, see
// https://github.com/lit/lit/issues/1891
function getCharsStr(chars) {
    return msg(str `${chars} ${chars > 1 ? 'characters' : 'character'}`);
}
//# sourceMappingURL=textarea.js.map